import { useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckIcon,
  ListChecksIcon,
  NotePencilIcon,
  TableIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Num, T, useGT } from 'gt-react';
import { toast } from 'sonner';
import {
  useWizard,
  type WizardStepDef,
  type WizardValidationResult,
} from '@erp/ui/hooks/use-wizard';
import { workbookSourceSchema, type WorkbookSource } from '@erp/shared-types';
import { orpc, rpcClient } from '@/api/rpc/client';
import { NamespaceField } from '@/components/namespaces/NamespaceField';
import {
  namespaceIdFromSelectValue,
  unassignedNamespaceOption,
  UNASSIGNED_NAMESPACE_VALUE,
} from '@/components/namespaces/namespaceLabels';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Wizard,
  WizardAssist,
  WizardField,
  WizardFooter,
  WizardHeader,
  WizardPanel,
  WizardRail,
  WizardSurface,
} from '@/components/ui/wizard';
import { useChatAgentWizardAdapter } from '@/components/wizard/chatAgentWizardAdapter';
import { AddInputPanel } from './WorkbookAddDataPanel';
import {
  WORKBOOK_NAME_MAX_LENGTH,
  wizardDraftDefinition,
  wizardHasContent,
  wizardWorkbookDefinition,
  workbookNameProblem,
} from './workbookCreateDraft';
import { requestWorkbookGeneration } from './workbookGenerationRequest';

type Gt = ReturnType<typeof useGT>;

function text(values: Record<string, unknown>, key: string): string {
  const value = values[key];
  return typeof value === 'string' ? value : '';
}

function wizardSteps(gt: Gt): WizardStepDef[] {
  return [
    {
      id: 'name',
      title: gt('Name', { context: 'new workbook wizard step title' }),
      description: gt('Name the workbook and file it under a namespace.', {
        context: 'new workbook wizard step description',
      }),
      icon: NotePencilIcon,
      agentBrief:
        'Suggest a short, descriptive workbook name (field "name") when the user describes what they want.',
      validate: ({ values }): WizardValidationResult => {
        const problem = workbookNameProblem(text(values, 'name'));
        if (!problem) return true;
        return {
          ok: false,
          fields: {
            name:
              problem === 'missing'
                ? gt('Name the workbook.', {
                    context: 'new workbook wizard name required',
                  })
                : gt('That name is too long.', {
                    context: 'new workbook wizard name too long',
                  }),
          },
        };
      },
    },
    {
      id: 'data',
      title: gt('Data', { context: 'new workbook wizard step title' }),
      description: gt(
        'Choose the data the workbook reads. You can add more later.',
        { context: 'new workbook wizard step description' },
      ),
      icon: TableIcon,
      optional: true,
      agentBrief:
        'The user is choosing data in the catalog on screen; the chosen data is listed in selectedData. Help them decide what the workbook needs; you cannot add data yourself.',
    },
    {
      id: 'instructions',
      title: gt('Instructions', { context: 'new workbook wizard step title' }),
      description: gt(
        'Describe what the workbook should do, step by step. The assistant builds the process from it.',
        { context: 'new workbook wizard step description' },
      ),
      icon: ListChecksIcon,
      optional: true,
      agentBrief:
        'Help the user write clear, numbered step-by-step instructions (field "instructions") for what the workbook should do with the selected data.',
    },
  ];
}

/** The data chosen so far, each removable; says so when there is none yet. */
function ChosenData({
  sources,
  onRemove,
}: {
  sources: readonly WorkbookSource[];
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  return (
    <section
      aria-label={gt('Chosen data', {
        context: 'new workbook wizard list of chosen data',
      })}
      className="space-y-1.5 rounded-lg border bg-muted/30 px-3 py-2"
    >
      <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <T context="new workbook wizard heading over the chosen data">
          Added to this workbook
        </T>
        <span className="tabular-nums" aria-live="polite">
          <Num>{sources.length}</Num>
        </span>
      </h3>
      {sources.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="new workbook wizard: no data chosen yet">
            Nothing yet. Pick data below and add it.
          </T>
        </p>
      ) : (
        <ChosenDataList sources={sources} onRemove={onRemove} />
      )}
    </section>
  );
}

function ChosenDataList({
  sources,
  onRemove,
}: {
  sources: readonly WorkbookSource[];
  onRemove: (id: string) => void;
}) {
  const gt = useGT();
  return (
    <ul className="flex flex-wrap gap-1.5">
      {sources.map((source) => (
        <li
          key={source.id}
          className="flex items-center gap-1 rounded-md bg-background py-0.5 pr-0.5 pl-2 text-sm shadow-xs ring-1 ring-border"
        >
          <CheckIcon className="size-3.5 text-primary" aria-hidden />
          {source.name}
          <Button
            size="icon"
            variant="ghost"
            className="size-6"
            aria-label={gt('Remove {name}', {
              name: source.name,
              context: 'new workbook wizard remove chosen data',
            })}
            onClick={() => onRemove(source.id)}
          >
            <XIcon />
          </Button>
        </li>
      ))}
    </ul>
  );
}

type CreatedWorkbook = Awaited<ReturnType<typeof rpcClient.workbooks.create>>;

/** Creates the Workbook with what the wizard collected, then opens it. */
function useCreateWorkbook(sources: readonly WorkbookSource[]) {
  const gt = useGT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // A Workbook created before its first save failed: Finish again retries
  // the save, with the name and namespace as they are now, rather than
  // creating another.
  const createdRef = useRef<CreatedWorkbook | null>(null);
  async function create(values: Record<string, unknown>) {
    setError(null);
    const name = text(values, 'name').trim();
    const namespaceId = namespaceIdFromSelectValue(
      text(values, 'namespace') || UNASSIGNED_NAMESPACE_VALUE,
    );
    const instructions = text(values, 'instructions');
    let workbookId: string;
    try {
      const retried = createdRef.current;
      const created =
        retried ?? (await rpcClient.workbooks.create({ name, namespaceId }));
      createdRef.current = created;
      workbookId = created.id;
      if (retried)
        await rpcClient.workbooks.setNamespace({ workbookId, namespaceId });
      if (retried || wizardHasContent(sources, instructions))
        await rpcClient.workbooks.save({
          workbookId,
          expectedRevision: created.revision,
          definition: {
            ...wizardWorkbookDefinition(
              created.definition,
              sources,
              instructions,
            ),
            name,
          },
        });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : gt('The workbook could not be created.', {
              context: 'new workbook wizard create error',
            }),
      );
      return;
    }
    void queryClient.invalidateQueries({
      queryKey: orpc.workbooks.list.key(),
    });
    if (instructions.trim()) requestWorkbookGeneration(workbookId);
    await navigate({
      to: '/workbooks/$workbookId',
      params: { workbookId },
    });
  }
  return { create, error };
}

function WorkbookCreateWizard({ onCancel }: { onCancel: () => void }) {
  const gt = useGT();
  const [sources, setSources] = useState<WorkbookSource[]>([]);
  const catalog = useQuery(orpc.workbooks.catalog.queryOptions({ input: {} }));
  const creation = useCreateWorkbook(sources);
  const agent = useChatAgentWizardAdapter({
    currentPage: '/workbooks',
    supportsAutopilot: false,
    getExtraContext: () => ({
      task: 'Create a new workbook: a repeatable worksheet process over business data.',
      selectedData: sources.map((source) => source.name),
    }),
  });
  const wizard = useWizard({
    agent,
    steps: wizardSteps(gt),
    initialValues: {
      name: '',
      namespace: UNASSIGNED_NAMESPACE_VALUE,
      instructions: '',
    },
    onComplete: creation.create,
  });
  const name = text(wizard.values, 'name');

  return (
    <Wizard api={wizard} className="h-[680px]">
      <WizardRail />
      <WizardSurface>
        <WizardHeader />
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <WizardPanel stepId="name">
            <div className="flex max-w-md flex-col gap-4">
              <WizardField
                name="name"
                label={gt('Workbook name', {
                  context: 'new workbook wizard name field',
                })}
              >
                <Input
                  autoFocus
                  value={name}
                  maxLength={WORKBOOK_NAME_MAX_LENGTH}
                  onChange={(event) =>
                    wizard.setValue('name', event.target.value)
                  }
                  aria-label={gt('Workbook name', {
                    context: 'new workbook wizard name field',
                  })}
                />
              </WizardField>
              <NamespaceField
                value={
                  text(wizard.values, 'namespace') || UNASSIGNED_NAMESPACE_VALUE
                }
                onChange={(next) => wizard.setValue('namespace', next)}
                noneOption={unassignedNamespaceOption(gt)}
                label={
                  <T context="new workbook wizard namespace field">Namespace</T>
                }
                description={
                  <T context="new workbook wizard namespace hint">
                    Optional. Groups the workbook on the Workbooks page.
                  </T>
                }
              />
            </div>
          </WizardPanel>
          <WizardPanel stepId="data">
            <div className="flex flex-col gap-3">
              <ChosenData
                sources={sources}
                onRemove={(id) =>
                  setSources((current) =>
                    current.filter((source) => source.id !== id),
                  )
                }
              />
              <AddInputPanel
                embedded
                editor={{
                  workbookId: null,
                  definition: wizardDraftDefinition(name, sources),
                  catalog: { data: catalog.data, error: catalog.error },
                }}
                onSource={(next) => {
                  const validated = workbookSourceSchema.safeParse(next);
                  if (!validated.success) return false;
                  setSources((current) => [...current, validated.data]);
                  toast.success(
                    gt('Added {name}', {
                      name: validated.data.name,
                      context: 'new workbook wizard: data was added',
                    }),
                  );
                  return true;
                }}
              />
            </div>
          </WizardPanel>
          <WizardPanel stepId="instructions">
            <WizardField
              name="instructions"
              label={gt('What should this workbook do?', {
                context: 'new workbook wizard instructions field',
              })}
              hint={gt(
                'For example: 1. Read the items. 2. Keep the ones whose minimum expired. 3. Total them by branch.',
                { context: 'new workbook wizard instructions example' },
              )}
            >
              <Textarea
                rows={12}
                value={text(wizard.values, 'instructions')}
                onChange={(event) =>
                  wizard.setValue('instructions', event.target.value)
                }
                aria-label={gt('What should this workbook do?', {
                  context: 'new workbook wizard instructions field',
                })}
              />
            </WizardField>
          </WizardPanel>
        </div>
        <WizardFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            <T context="button to abandon the new workbook wizard">Cancel</T>
          </Button>
          {creation.error && (
            <p role="alert" className="text-sm text-destructive">
              {creation.error}
            </p>
          )}
        </WizardFooter>
      </WizardSurface>
      <WizardAssist />
    </Wizard>
  );
}

/**
 * New workbook: name it, choose its data, and say what it should do; finishing
 * creates it with that data and those instructions, opens it, and starts the
 * assistant generating its Process.
 */
export function WorkbookCreateWizardDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(96vw,80rem)] overflow-hidden p-0 sm:max-w-[min(96vw,80rem)]">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle>
            <T context="new workbook wizard dialog title">New workbook</T>
          </DialogTitle>
        </DialogHeader>
        {/* Mounted only while open, so each opening starts empty. */}
        {open && <WorkbookCreateWizard onCancel={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}
